<script setup lang="ts">
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';

import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { formatDuration, formatShortDateTime } from '@/lib/commonFunctions';
import {
    listSlideExports,
    type SlideExportManifestItem,
    type SlideExportWarning
} from '@/services/slideStudio';
import { useTenantStore } from '@/stores/tenant';

const tenantStore = useTenantStore();

const PAGE_SIZE = 50;

const exports = ref<SlideExportManifestItem[]>([]);
const loading = ref(true);
const loadingMore = ref(false);
// A short page means the archive is exhausted: the backend pages over blobs, so a
// full page is the only evidence that another one exists.
const hasMore = ref(false);
const loadError = ref<string | null>(null);
const expandedExportId = ref<string | null>(null);

async function loadExports() {
    loading.value = true;
    loadError.value = null;
    try {
        // The caller's own tenant (Elqano) — required by the auth gate, not a
        // result filter: the listing spans all tenants.
        exports.value = await listSlideExports(
            tenantStore.tenantId || '',
            PAGE_SIZE
        );
        hasMore.value = exports.value.length === PAGE_SIZE;
    } catch (error) {
        console.error(error);
        loadError.value = 'Failed to load exports';
    }
    loading.value = false;
}

async function loadMore() {
    loadingMore.value = true;
    loadError.value = null;
    try {
        const page = await listSlideExports(
            tenantStore.tenantId || '',
            PAGE_SIZE,
            exports.value.length
        );
        exports.value = [...exports.value, ...page];
        hasMore.value = page.length === PAGE_SIZE;
    } catch (error) {
        console.error(error);
        loadError.value = 'Failed to load more exports';
    }
    loadingMore.value = false;
}

onMounted(loadExports);

function warningSummary(warnings: SlideExportWarning[]): string {
    const byCode = new Map<string, number>();
    for (const w of warnings) {
        byCode.set(w.code, (byCode.get(w.code) || 0) + 1);
    }
    return Array.from(byCode.entries())
        .map(([code, count]) => (count > 1 ? `${code} ×${count}` : code))
        .join(', ');
}

function warningDetail(w: SlideExportWarning): string {
    const parts: string[] = [];
    if (w.slide_index != null) parts.push(`slide ${w.slide_index + 1}`);
    if (w.element_type) parts.push(w.element_type);
    if (w.reason) parts.push(w.reason);
    if (w.message) parts.push(w.message);
    if (w.detail) parts.push(w.detail);
    if (w.src || w.path) parts.push(w.src || w.path || '');
    return parts.join(' — ');
}

function toggleExpanded(item: SlideExportManifestItem) {
    expandedExportId.value =
        expandedExportId.value === item.export_id ? null : item.export_id;
}

const hasRows = computed(() => exports.value.length > 0);
</script>

<template>
    <div class="flex w-full flex-col gap-4">
        <div class="flex items-center justify-between">
            <div>
                <h2 class="text-xl font-semibold">Slide exports</h2>
                <p class="text-sm text-muted-foreground">
                    Recent server-side exports across all workspaces (status,
                    timings, per-slide warnings). Archived 30 days for debugging
                    and quality checks.
                </p>
            </div>
            <Button variant="outline" :disabled="loading" @click="loadExports">
                <RefreshCw class="mr-2 h-4 w-4" />
                Refresh
            </Button>
        </div>

        <div v-if="loading" class="flex justify-center py-16">
            <Loader2 class="h-6 w-6 animate-spin text-muted-foreground" />
        </div>

        <p
            v-else-if="loadError && !hasRows"
            class="py-8 text-center text-destructive"
        >
            {{ loadError }}
        </p>

        <p v-else-if="!hasRows" class="py-8 text-center text-muted-foreground">
            No exports recorded yet.
        </p>

        <Table v-else>
            <TableHeader>
                <TableRow>
                    <TableHead class="w-8" />
                    <TableHead>Date</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Document</TableHead>
                    <TableHead>Format</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead class="text-right">Slides</TableHead>
                    <TableHead class="text-right">Duration</TableHead>
                    <TableHead>Warnings</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                <template v-for="item in exports" :key="item.export_id">
                    <TableRow
                        class="cursor-pointer"
                        @click="toggleExpanded(item)"
                    >
                        <TableCell class="text-muted-foreground">
                            <ChevronDown
                                v-if="expandedExportId === item.export_id"
                                class="h-4 w-4"
                            />
                            <ChevronRight v-else class="h-4 w-4" />
                        </TableCell>
                        <TableCell class="whitespace-nowrap">
                            {{ formatShortDateTime(item.created_at) }}
                        </TableCell>
                        <TableCell
                            class="max-w-44 truncate"
                            :title="item.tenant_id || undefined"
                        >
                            {{ item.tenant_name || item.tenant_id || '—' }}
                        </TableCell>
                        <TableCell
                            class="max-w-56 truncate"
                            :title="item.document_id || undefined"
                        >
                            {{ item.document_name || item.document_id || '—' }}
                        </TableCell>
                        <TableCell class="uppercase">
                            {{ item.format || '—' }}
                        </TableCell>
                        <TableCell>
                            <span
                                class="rounded-full px-2 py-0.5 text-xs font-medium"
                                :class="
                                    item.status === 'SUCCESS'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                "
                            >
                                {{ item.status || 'UNKNOWN' }}
                            </span>
                        </TableCell>
                        <TableCell class="text-right">
                            {{ item.slide_count ?? '—' }}
                        </TableCell>
                        <TableCell class="text-right">
                            {{ formatDuration(item.duration_s) }}
                        </TableCell>
                        <TableCell
                            class="max-w-64 truncate text-muted-foreground"
                            :title="warningSummary(item.warnings)"
                        >
                            {{
                                item.warnings.length
                                    ? warningSummary(item.warnings)
                                    : '—'
                            }}
                        </TableCell>
                    </TableRow>
                    <TableRow v-if="expandedExportId === item.export_id">
                        <TableCell />
                        <TableCell colspan="8" class="bg-muted/40">
                            <div class="flex flex-col gap-2 py-2 text-sm">
                                <p
                                    class="font-mono text-xs text-muted-foreground"
                                >
                                    export {{ item.export_id }} — document
                                    {{ item.document_id || '?' }}
                                </p>
                                <p
                                    v-if="item.error"
                                    class="whitespace-pre-wrap font-mono text-xs text-destructive"
                                >
                                    {{ item.error }}
                                </p>
                                <ul
                                    v-if="item.warnings.length"
                                    class="flex list-disc flex-col gap-1 pl-5"
                                >
                                    <li
                                        v-for="(w, i) in item.warnings"
                                        :key="i"
                                        class="font-mono text-xs"
                                    >
                                        <span class="font-semibold">{{
                                            w.code
                                        }}</span>
                                        <template v-if="warningDetail(w)">
                                            — {{ warningDetail(w) }}
                                        </template>
                                    </li>
                                </ul>
                                <p
                                    v-else-if="!item.error"
                                    class="text-muted-foreground"
                                >
                                    No warnings.
                                </p>
                            </div>
                        </TableCell>
                    </TableRow>
                </template>
            </TableBody>
        </Table>

        <p
            v-if="loadError && hasRows"
            class="text-center text-sm text-destructive"
        >
            {{ loadError }}
        </p>

        <div v-if="hasMore && !loading" class="flex justify-center">
            <Button variant="outline" :disabled="loadingMore" @click="loadMore">
                <Loader2 v-if="loadingMore" class="mr-2 h-4 w-4 animate-spin" />
                Load more
            </Button>
        </div>
    </div>
</template>
